import { useTranslation } from 'react-i18next';
import { useFormState, type UseFormReturn } from 'react-hook-form';
import { Truck } from 'lucide-react';

import { Input } from '@/components/ui/input';
import type { ReceiveConvoyShipmentDto } from '@/entities/receive';
import { CONFIRM_ARRIVAL_TEXT_MAX_LENGTH, ConfirmArrivalWarnings } from '@/features/receive-confirm-arrival';

import type { ReceiveConvoyFormValues } from '../model/types';

/** A truck's header row: vehicle, waybill, and its dangerous-goods warnings. */
export function ReceiveConvoyTruckHeader({ shipment }: { shipment: ReceiveConvoyShipmentDto }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
        <Truck className="h-4 w-4 text-muted-foreground" aria-hidden />
        {shipment.vehicleIdentifier || '—'}
        <span className="font-mono font-normal text-muted-foreground">{shipment.waybill || '—'}</span>
      </span>
      <ConfirmArrivalWarnings warnings={shipment.warnings} />
    </div>
  );
}

interface ReceiveConvoyTruckDocumentsProps {
  form: UseFormReturn<ReceiveConvoyFormValues>;
  shipment: ReceiveConvoyShipmentDto;
  shipmentIndex: number;
}

/**
 * Below a truck's lines: what is stored once per receipt — the transport
 * document and the comments. Legacy showed both on every row but kept only the
 * first row's; here they sit where they are actually stored, and below the
 * lines so they never stand between the lines and the column headers.
 */
export function ReceiveConvoyTruckDocuments({ form, shipment, shipmentIndex }: ReceiveConvoyTruckDocumentsProps) {
  const { t } = useTranslation();
  const { errors } = useFormState({ control: form.control, name: `shipments.${shipmentIndex}` });
  const shipmentErrors = errors.shipments?.[shipmentIndex];
  const transportDocId = `convoy-${shipment.shipmentId}-transportDocNumber`;
  const commentsId = `convoy-${shipment.shipmentId}-comments`;

  return (
    <div className="grid grid-cols-1 gap-3 font-normal sm:grid-cols-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={transportDocId} className="text-xs text-muted-foreground">
          {t('receive.document.label', 'Transport Documentation Number')}
        </label>
        <Input
          {...form.register(`shipments.${shipmentIndex}.transportDocNumber`)}
          id={transportDocId}
          maxLength={CONFIRM_ARRIVAL_TEXT_MAX_LENGTH}
          placeholder={t('receive.document.desc', 'I.e. Way Bill Number, Goods receive Note Number, etc...')}
        />
        {shipmentErrors?.transportDocNumber?.message && (
          <p className="text-xs text-destructive">{shipmentErrors.transportDocNumber.message}</p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor={commentsId} className="text-xs text-muted-foreground">
          {t('receive.comments.label', 'Comments')}
        </label>
        <Input
          {...form.register(`shipments.${shipmentIndex}.comments`)}
          id={commentsId}
          maxLength={CONFIRM_ARRIVAL_TEXT_MAX_LENGTH}
          placeholder={t('receive.comments.label', 'Comments')}
        />
        {shipmentErrors?.comments?.message && (
          <p className="text-xs text-destructive">{shipmentErrors.comments.message}</p>
        )}
      </div>
    </div>
  );
}
